import { useCallback, useEffect, useRef, useState } from "react";
import {
  iframeMountScheduler,
  type IframeMountHandle,
} from "../../../lib/iframeMountScheduler";

/** How an HTML card decides when its iframe may mount. */
export type IframeMountPolicy = "eager" | "progressive";

export interface UseIframeMountAdmissionParams {
  /** `"progressive"` paces the mount through the shared scheduler;
   *  `"eager"` (the editor default) admits immediately — byte-identical to
   *  the pre-scheduler behavior. */
  policy: IframeMountPolicy;
  /** Card host element the scheduler observes for viewport proximity. */
  hostRef: React.RefObject<Element | null>;
  /** The card's document is prepared and could mount right now (i.e.
   *  `displayDoc !== null`). Progressive cards enter the scheduler only once
   *  ready, so a granted slot always paces a real iframe mount and the load
   *  event is what returns it. Registering earlier would burn slots on cards
   *  whose fetch is still in flight — their slots would leak back via the
   *  fallback timeout, and the mounts would then cluster un-paced whenever
   *  the fetches resolve. */
  ready?: boolean;
}

export interface UseIframeMountAdmissionResult {
  /** Render the iframe only when true. */
  admitted: boolean;
  /** Wire to the iframe's `onLoad` — frees the scheduler's concurrency slot.
   *  Safe (no-op) under the eager policy. */
  onIframeLoad: () => void;
}

/**
 * Card-side face of the iframe mount scheduler (see iframeMountScheduler.ts
 * for why mounts are paced at all). Admission is one-way: once granted it is
 * never revoked, so an admitted card keeps its live iframe on scroll-away.
 */
export function useIframeMountAdmission({
  policy,
  hostRef,
  ready = true,
}: UseIframeMountAdmissionParams): UseIframeMountAdmissionResult {
  const progressive = policy === "progressive";
  const [admitted, setAdmitted] = useState(false);
  const handleRef = useRef<IframeMountHandle | null>(null);

  useEffect(() => {
    if (!progressive || !ready) return;
    const el = hostRef.current;
    if (!el) {
      // Fail OPEN. A ref object's identity never changes, so this effect has
      // no way to re-run when the element attaches later — a closed gate here
      // would strand the card on its placeholder forever, with no error and
      // no timeout. An unpaced mount beats a silently blank card.
      setAdmitted(true);
      return;
    }
    const handle = iframeMountScheduler.register(el, () => setAdmitted(true));
    handleRef.current = handle;
    return () => {
      handleRef.current = null;
      handle.release();
    };
  }, [progressive, ready, hostRef]);

  const onIframeLoad = useCallback(() => {
    handleRef.current?.loaded();
  }, []);

  // Eager always renders. (Admission state is one-way — nothing un-sets it —
  // so a granted slot survives everything short of unmount.)
  return { admitted: admitted || !progressive, onIframeLoad };
}
